// ─────────────────────────────────────────────────────────────────────────────
// Data layer for /questionnaires — pure RPC.
//
// Rules:
//   1. Each function makes ONE HTTP call and returns the raw response.
//   2. No param encoding, no JSON building, no offset arithmetic — callers pass
//      already-built query strings / bodies.
//   3. No response normalisation, no `{ data: ... }` unwrap — return the raw
//      shape; hooks normalise.
//
// Everything else (param→qs encoding, request body assembly, response
// normalisation) lives in the corresponding `-hooks/use*.ts` file.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  QuestionnaireRequestsParams,
  QuestionnaireRequestsResponse,
  QuestionnaireTemplatesResponse,
  SendQuestionnaireInviteItem,
  QuestionnaireTemplateBasic as SharedQuestionnaireTemplate,
} from '@/api/questionnaires/types';
import { API_ROOT_URL, QUESTIONNAIRE_SERVICE_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  AssetTypeOption,
  BuilderMode,
  BulkInviteRequestItem,
  CreatedQuestionnaire,
  CreateQuestionnairePayload,
  CreateTemplatePayload,
  DataPoint,
  DataPointTemplateGroup,
  ImpactAreaHierarchyNode,
  MyQuestionnaireRequestsResponse,
  MyTemplatesResponse,
  Questionnaire,
  QuestionnaireResponsesResponse,
  QuestionnairesResponse,
  QuestionnaireTemplate,
  QuestionnaireTemplatesListResponse,
  RawBuilderDetail,
} from './types';

export async function fetchQuestionnaireRequests(
  params: QuestionnaireRequestsParams,
): Promise<QuestionnaireRequestsResponse> {
  const qs = new URLSearchParams();
  qs.set('companyId', params.companyId);
  qs.set('page', String(params.page - 1));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return apiFetch<QuestionnaireRequestsResponse>(
    `/v1/requests?${qs.toString()}`,
    {},
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function fetchQuestionnaireTemplates(): Promise<
  SharedQuestionnaireTemplate[]
> {
  const qs = new URLSearchParams({
    page: '0',
    filter: JSON.stringify({ isTemplate: 0, isArchived: 0 }),
    count: '500',
    isTemplate: 'false',
  });
  const res = await apiFetch<QuestionnaireTemplatesResponse>(
    `/v1/questionnaires?${qs.toString()}`,
    {},
    QUESTIONNAIRE_SERVICE_URL,
  );
  return res.data;
}

export async function sendQuestionnaireInvites(
  payload: SendQuestionnaireInviteItem[],
): Promise<void> {
  await apiFetch<void>(
    '/v1/questionnaires/requests',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

// ─── Questionnaires list ────────────────────────────────────────────────────

export async function fetchQuestionnaires(
  qs: string,
): Promise<QuestionnairesResponse> {
  return apiFetch<QuestionnairesResponse>(
    `/v1/questionnaires?${qs}`,
    {},
    QUESTIONNAIRE_SERVICE_URL,
  );
}

/** Per-questionnaire request list — one row per company that received
 *  this questionnaire, with per-request status + answer statistics.
 *  Backs the Summary subtab's companies table and the Review subtab's
 *  master rail. */
export async function fetchQuestionnaireResponsesList(
  questionnaireId: string,
  qs: string,
): Promise<QuestionnaireResponsesResponse> {
  return apiFetch<QuestionnaireResponsesResponse>(
    `/v1/questionnaires/${questionnaireId}/requests?${qs}`,
    {},
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function createQuestionnaire(
  payload: CreateQuestionnairePayload,
): Promise<CreatedQuestionnaire> {
  return apiFetch<CreatedQuestionnaire>(
    '/v1/questionnaires',
    { method: 'POST', body: JSON.stringify(payload) },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function archiveQuestionnaires(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/v1/questionnaires/archive',
    { method: 'POST', body: JSON.stringify(ids) },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function unarchiveQuestionnaires(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/v1/questionnaires/unarchive',
    { method: 'POST', body: JSON.stringify(ids) },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function deleteQuestionnaires(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/v1/questionnaires',
    { method: 'DELETE', body: JSON.stringify(ids) },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

// Bulk operations on per-company answer requests (Responses → Summary
// page). All four endpoints scope by `questionnaireId` + a list of
// `requestIds`. Endpoint paths mirror the legacy service.
interface BulkRequestArgs {
  questionnaireId: string;
  requestIds: string[];
}

export async function bulkDeleteAnswerRequests(
  args: BulkRequestArgs,
): Promise<void> {
  await apiFetch<void>(
    '/v1/questionnaires/requests',
    { method: 'DELETE', body: JSON.stringify(args) },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function bulkUpdateAnswerRequestsDueDate(
  args: BulkRequestArgs & { dueDate: string | null },
): Promise<void> {
  await apiFetch<void>(
    '/v1/questionnaires/requests/due-date',
    { method: 'PATCH', body: JSON.stringify(args) },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function sendAnswerRequestsReminder(
  args: BulkRequestArgs,
): Promise<void> {
  await apiFetch<void>(
    '/v1/questionnaires/requests/reminder',
    { method: 'PATCH', body: JSON.stringify(args) },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function exportAnswerRequests(args: {
  questionnaireId: string;
  /** When true, the server ignores `requestIds` and exports every
   *  request on the questionnaire. */
  allRequests: boolean;
  requestIds: string[];
}): Promise<void> {
  await apiFetch<void>(
    `/v1/questionnaires/${args.questionnaireId}/export`,
    {
      method: 'POST',
      body: JSON.stringify({
        allRequests: args.allRequests,
        requestIds: args.requestIds,
      }),
    },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function cloneQuestionnaireAsTemplate(
  id: string,
): Promise<Questionnaire> {
  return apiFetch<Questionnaire>(
    `/v1/questionnaires/${id}/save-as-template`,
    { method: 'POST' },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function fetchQuestionnaireTemplatesList(): Promise<QuestionnaireTemplatesListResponse> {
  return apiFetch<QuestionnaireTemplatesListResponse>(
    '/v1/templates',
    {},
    QUESTIONNAIRE_SERVICE_URL,
  );
}

// ─── Requests ───────────────────────────────────────────────────────────────

export async function fetchMyQuestionnaireRequests(
  qs: string,
): Promise<MyQuestionnaireRequestsResponse> {
  return apiFetch<MyQuestionnaireRequestsResponse>(
    `/v1/requests?${qs}`,
    {},
    QUESTIONNAIRE_SERVICE_URL,
  );
}

// ─── Templates ──────────────────────────────────────────────────────────────

export async function fetchMyTemplates(
  qs: string,
): Promise<MyTemplatesResponse> {
  return apiFetch<MyTemplatesResponse>(
    `/v1/templates?${qs}`,
    {},
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function createQuestionnaireTemplate(
  payload: CreateTemplatePayload,
): Promise<QuestionnaireTemplate> {
  return apiFetch<QuestionnaireTemplate>(
    '/v1/templates',
    { method: 'POST', body: JSON.stringify(payload) },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function duplicateQuestionnaireTemplate(
  id: string,
): Promise<QuestionnaireTemplate> {
  return apiFetch<QuestionnaireTemplate>(
    `/v1/templates/${id}/copy`,
    { method: 'POST' },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function deleteQuestionnaireTemplates(
  ids: string[],
): Promise<void> {
  await apiFetch<void>(
    '/v1/templates',
    { method: 'DELETE', body: JSON.stringify(ids) },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

// ─── Builder ────────────────────────────────────────────────────────────────

export type BuilderDetailEnvelope =
  | RawBuilderDetail
  | { data: RawBuilderDetail };

export async function fetchBuilderDetail(
  mode: BuilderMode,
  id: string,
): Promise<BuilderDetailEnvelope> {
  const url =
    mode === 'template'
      ? `/v1/templates/${id}`
      : `/v1/questionnaires/builder/${id}`;
  return apiFetch<BuilderDetailEnvelope>(url, {}, QUESTIONNAIRE_SERVICE_URL);
}

export async function saveBuilderDetail(
  mode: BuilderMode,
  id: string,
  body: Record<string, unknown>,
): Promise<BuilderDetailEnvelope> {
  const url =
    mode === 'template'
      ? `/v1/templates/${id}`
      : `/v1/questionnaires/builder/${id}`;
  return apiFetch<BuilderDetailEnvelope>(
    url,
    { method: 'PUT', body: JSON.stringify(body) },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

// ─── Question Library ───────────────────────────────────────────────────────

/** PATCH a batch of datapoint answers back to the company profile under
 *  a single template (e.g. `WASTE`, `ENERGY_SOURCES`, `ABOUT`). The
 *  questionnaire-side "Sync profile data" button fires one of these per
 *  template grouping. Mirrors the legacy `patchCompanyProfiles` call. */
export async function syncTemplateAnswersToProfile(
  templateId: string,
  body: {
    answer: Record<string, unknown>;
    forCompanyId: string;
    byCompanyId: string;
    onBehalfOfCompanyId: string;
    meta: {
      source: {
        from: 'questionnaire-sync';
        id: string;
        forCompanyId: string;
        requestId: string;
      };
    };
  },
): Promise<void> {
  // The template-answer route lives on the main API gateway
  // (`/api/companies/templates/:templateId/answer`), NOT on the
  // companies-service microservice — mirrors `patchProfileTemplate` in
  // `@/api/companies`. Hitting `COMPANIES_SERVICE_URL` + `/v1/templates/...`
  // 404s with `Route PATCH:/companies-service/v1/templates/.../answer not found`.
  await apiFetch<void>(
    `/api/companies/templates/${templateId.toLowerCase()}/answer`,
    { method: 'PATCH', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

// Lives on the main API gateway (`/api/companies/impact-areas/hierarchy/:sectionId`),
// NOT on the companies-service microservice — mirrors `patchProfileTemplate`.
export async function fetchImpactAreasHierarchy(
  sectionId: number,
): Promise<ImpactAreaHierarchyNode[]> {
  const res = await apiFetch<ImpactAreaHierarchyNode[]>(
    `/api/companies/impact-areas/hierarchy/${sectionId}`,
    {},
    API_ROOT_URL,
  );
  return res ?? [];
}

export async function fetchDataPointTemplates(): Promise<
  DataPointTemplateGroup[]
> {
  const res = await apiFetch<DataPointTemplateGroup[]>(
    '/v1/data-points?groupByTemplates=true',
    {},
    QUESTIONNAIRE_SERVICE_URL,
  );
  return res ?? [];
}

/** Flat list of every data-point definition. Used by the preview page to
 *  resolve options for datapoint-bound questions whose options were not
 *  inlined on save — covers datapoints that aren't part of any impact-area
 *  template (e.g. network / connection-type datapoints) and therefore
 *  don't appear in the template-grouped response. */
export async function fetchAllDataPoints(): Promise<DataPoint[]> {
  const res = await apiFetch<DataPoint[]>(
    '/v1/data-points',
    {},
    QUESTIONNAIRE_SERVICE_URL,
  );
  return res ?? [];
}

// ─── Extra datapoint option sources (preview-only) ──────────────────────────
//
// A handful of legacy datapoints (`facility_processes`, `product_types`,
// `product_levels`, `relationship_type`) resolve their option lists from
// dedicated endpoints rather than the datapoint definition. The preview
// page calls these to backfill option pickers that would otherwise come
// up empty.

interface FacilityProcessesResponse {
  facilities?: Array<{
    productionProcessId?: string;
    productionProcessName?: string;
    children?: Array<{ key: string; text: string }>;
  }>;
}

export async function fetchFacilityProcessOptions(): Promise<
  Array<{ label: string; id: string; group?: string }>
> {
  // Backend handler returns `{ facilities: [...] }` at the top level
  // (no `{data: ...}` envelope) — `apiFetch` hands us the parsed body
  // verbatim, so read `facilities` directly.
  const res = await apiFetch<FacilityProcessesResponse>(
    '/v3/facility-processes/drop-down',
  );
  const facilities = res?.facilities ?? [];
  const out: Array<{ label: string; id: string; group?: string }> = [];
  for (const facility of facilities) {
    const group = facility.productionProcessName ?? undefined;
    for (const child of facility.children ?? []) {
      out.push({
        id: child.key,
        label: child.text,
        ...(group ? { group } : {}),
      });
    }
  }
  return out;
}

interface ProductCategoriesResponse {
  // `reply.paginate(...)` puts the rows at top-level `data`.
  data?: Array<{
    id: string;
    name: string;
    parentId?: string | null;
  }>;
}

export async function fetchProductCategoryOptions(): Promise<{
  productTypes: Array<{ label: string; id: string }>;
  productLevels: Array<{ label: string; id: string }>;
}> {
  const res = await apiFetch<ProductCategoriesResponse>(
    '/v3/product-categories?isDropDown=true',
  );
  const rows = res?.data ?? [];
  return {
    productTypes: rows
      .filter(r => r.parentId)
      .map(r => ({ id: r.id, label: r.name })),
    productLevels: rows
      .filter(r => !r.parentId)
      .map(r => ({ id: r.id, label: r.name })),
  };
}

interface RelationshipTypesResponse {
  data?: Array<{ id: string; name: string }>;
}

export async function fetchRelationshipTypeOptions(): Promise<
  Array<{ label: string; id: string }>
> {
  const res = await apiFetch<RelationshipTypesResponse>(
    '/v3/relationship-types',
  );
  return (res?.data ?? []).map(r => ({ id: r.id, label: r.name }));
}

// ─── Asset type options (DOCUMENTS / AUDIT / CERTIFICATE) ───────────────────

export async function fetchAssetTypeOptions(): Promise<AssetTypeOption[]> {
  const res = await apiFetch<AssetTypeOption[]>(
    '/v3/assessment-forms/question-asset-link',
  );
  return res ?? [];
}

// ─── Questionnaire status ────────────────────────────────────────────────────

export async function patchQuestionnaireStatus(
  id: string,
  status: 'ACTIVE' | 'DRAFT' | 'COMPLETED',
): Promise<void> {
  await apiFetch<void>(
    `/v1/questionnaires/${id}`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

// ─── Bulk-invite companies onto a questionnaire ─────────────────────────────

/** Sends one POST that creates a per-company request row for each item.
 *  Backend returns 204 on success; non-2xx throws via `apiFetch` so the
 *  caller surfaces a toast. 409 means at least one selected company
 *  already has an open request for this questionnaire — the server
 *  rejects the whole batch in that case. */
export async function bulkInviteCompanies(
  items: BulkInviteRequestItem[],
): Promise<void> {
  await apiFetch<void>(
    '/v1/questionnaires/requests',
    {
      method: 'POST',
      body: JSON.stringify(items),
    },
    QUESTIONNAIRE_SERVICE_URL,
  );
}
